<template>
  <div class="mx-auto max-w-5xl p-4">
    <p class="text-sm opacity-80 mb-3">
      Prosím, označte, nakoľko je každé tvrdenie typické pre vás pri učení.
    </p>
    <div class="text-left mb-3">
      <label class="inline-flex items-start gap-2">
        <input type="checkbox" v-model="agreement" :true-value="1" :false-value="0" />
        <span class="text-xs opacity-80">
          Súhlasím s anonymným spracovaním mojich odpovedí na vedecké účely.
        </span>
      </label>
    </div>

    <!-- RUNNING -->
    <div v-if="phase==='running'">
      <div class="table-wrapper">
        <table class="likert-table">
          <thead>
            <tr>
              <th class="th-num">Otázka</th>
              <th v-for="opt in likertRev" :key="'h-'+opt.value" class="col-opt">
                <div class="th-num">{{ opt.value }}</div>
                <div class="th-sub">{{ opt.head }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="q in questions" :key="'q-'+q.id">
              <td class="qtext">
                <span class="qid">{{ q.id }}.</span> {{ q.text }}
              </td>
              <td v-for="opt in likertRev" :key="'c-'+q.id+'-'+opt.value" class="opt-cell">
                <input type="radio"
                       :name="'q-'+q.id"
                       v-model.number="responses[q.id]"
                       :value="opt.value"
                       :aria-label="'Otázka '+q.id+', voľba '+opt.value" 
                       :disabled="isLocked" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between mt-4 btn-bar">
        <div class="text-xs opacity-80">
          Zodpovedané: {{ answeredCount }} / {{ questions.length }} 
        </div>
        <v-btn class="visible-disabled" :disabled="answeredCount<questions.length" color="primary" @click="submit" style="margin-top: -10px;">
          Odoslať
        </v-btn>
      </div>
    </div>

    <!-- FINISHED -->
    <div v-else-if="phase==='finished'" class="mt-4">
      <v-card outlined class="pa-3 mt-4">     
        <!-- Radar -->
        <div class="max-w-xl">
          <canvas ref="radarCanvas" aria-label="Radar metakognície" role="img"></canvas>
        </div>

        <div class="d-flex justify-end">
          <v-btn color="primary" type="submit" class="mr-2" @click="closeTest">Koniec</v-btn>
        </div>
      </v-card>

    </div>
  </div>
</template>

<script>
import axios from '../../axios-auth.js'
import {
  Chart, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend
} from 'chart.js';
Chart.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export default {
  name: "MetaCognitionLikert5Table",
  props: {
    beaconUrl: { type: String, default: "" },
    participantId: { type: [String, Number], default: "" }
  },
  data() {
    return {
      phase: 'ready',
      startTime: null,
      elapsedSec: 0,
      secTimer: null,
      finalized: false,
      radar: null,
      agreement : 0,

      // Likert: zobrazíme v poradí 5 -> 1
      likert: [
        { value: 1, head: 'vôbec nie' },
        { value: 2, head: 'skôr nie' },
        { value: 3, head: 'neutrálne' },
        { value: 4, head: 'skôr áno' },
        { value: 5, head: 'určite áno' },
      ],

      questions: [
        { id: 1,  text: 'Pred začatím úlohy si premyslím, čo sa skutočne potrebujem naučiť.', construct: 'Planning' },
        { id: 2,  text: 'Pred začatím úlohy si stanovujem konkrétne ciele.', construct: 'Planning' },
        { id: 3,  text: 'Viem, aký druh informácií je najdôležitejšie sa naučiť.', construct: 'Knowledge' },
        { id: 4,  text: 'Viem, čo učiteľ očakáva, že sa naučím.', construct: 'Knowledge' },
        { id: 5,  text: 'Mám kontrolu nad tým, ako dobre sa učím.', construct: 'Motivation' },
        { id: 6,  text: 'Pre pochopení dôležitých súvislostí mi pomáha ich pravidelné prechádzanie.', construct: 'Regulation' },
        { id: 7,  text: 'Po skončení si zhrniem, čo som sa naučil/la.', construct: 'Regulation' },
        { id: 8,  text: 'Dokážem sa motivovať k učeniu, keď to potrebujem.', construct: 'Motivation' },
        { id: 9,  text: 'Uvedomujem si, aké stratégie používam pri učení.', construct: 'Knowledge' },
        { id: 10, text: 'Viem posúdiť, ako dobre niečomu rozumiem.', construct: 'Regulation' },
        { id: 11, text: 'Zisťujem, že používam užitočné stratégie učenia sa automaticky.', construct: 'Knowledge' },
        { id: 12, text: 'Viem, kedy bude každá z mnou používaných stratégií najefektívnejšia.', construct: 'Knowledge' },
        { id: 13, text: 'Snažím sa preložiť nové informácie vlastnými slovami.', construct: 'Regulation' },
        { id: 14, text: 'Keď nerozumiem, mením stratégie.', construct: 'Regulation' },
        { id: 15, text: 'Pri učení mi pomáha využívanie organizačnej štruktúry textu.', construct: 'Regulation' },
        { id: 16, text: 'Pýtam sa sám/sama seba, či to, čo čítam, súvisí s tým, čo už viem.', construct: 'Regulation' },
        { id: 17, text: 'Keď sa cítim zmätene prehodnocujem svoje závery.', construct: 'Regulation' },
        { id: 18, text: 'Potom čo dokončím úlohu sa pýtam sa samého/samej seba, či som sa naučil/la toľko, koľko som sa mohol/la.', construct: 'Regulation' },
        { id: 19, text: 'Keď mi na novej informácií niečo nie je jasné, zastavím sa a vrátim sa k nej.', construct: 'Regulation' },
      ],

      responses: {}, // { [id:number]: 1..5 }
      scales: {
        Planning: null,
        Knowledge: null,
        Regulation: null,
        Motivation: null,
        Global: null
      }
    };
  },
  computed: {
    answeredCount() {
      return Object.keys(this.responses).length;
    },
    allIds() {
      return this.questions.map(function(q){ return String(q.id); });
    },
    likertRev() {
      // 5 -> 1
      return this.likert.slice().sort(function(a,b){ return b.value - a.value; });
    },
    isLocked() { return this.agreement !== 1; },
  },
  mounted() {
    this.startTime = Date.now()
    this.start()
  },
  methods: {
    // helpers
    valOr(v, fb) { return (v!=null ? v : fb); },
    toFixedSafe(x, d) { return (typeof x==='number' && isFinite(x)) ? x.toFixed(d) : ''; },
    fmt(v) { return (v==null || isNaN(v)) ? '—' : Number(v).toFixed(2); },

    start() {
      this.phase = 'running';
      this.responses = {};     
      this.elapsedSec = 0;
      if (this.secTimer) clearInterval(this.secTimer);
      this.secTimer = setInterval(() => { this.elapsedSec++; }, 1000);
      window.addEventListener('beforeunload', this.onBeforeUnload, true);
      window.addEventListener('pagehide', this.onPageHide, true);
    },
    restart() {
      this.cleanup();
      this.destroyRadar();
      this.phase = 'ready';
    },
    meanByKeys(keys) {
      const vals = keys.map(k => this.responses[String(k)]).filter(v => typeof v === 'number');
      if (!vals.length) return null;
      let s = 0; for (let i=0;i<vals.length;i++) s += vals[i];
      return s / vals.length;
    },
    computeScales() {
      this.scales.Planning   = this.meanByKeys(['1','2']);
      this.scales.Knowledge  = this.meanByKeys(['3','4','9','11','12']);
      this.scales.Regulation = this.meanByKeys(['6','7','10','13','14','15','16','17','18','19']);
      this.scales.Motivation = this.meanByKeys(['5','8']);
      this.scales.Global     = this.meanByKeys(this.allIds);
    },
    submit() {
      if (this.answeredCount < this.questions.length) return;
      if (this.secTimer) { clearInterval(this.secTimer); this.secTimer = null; }
      this.computeScales();
      this.phase = 'finished';
      this.$nextTick(() => this.drawRadar());

      const summary = this.summarize('completed');
      this.emitAndBeacon(summary);
      this.finalized = true;
      window.removeEventListener('beforeunload', this.onBeforeUnload, true);
      window.removeEventListener('pagehide', this.onPageHide, true);
    },
    summarize(end_reason) {
      this.computeScales();
      this.writeAnswer(end_reason)
      return {
        participant_id: this.participantId || null,
        end_reason,
        duration_sec: this.startTime ? Math.round((performance.now() - this.startTime)/1000) : null,
        n_items: this.questions.length,
        n_answered: this.answeredCount,
        scales: { ...this.scales },
        responses: { ...this.responses },
        client_time_iso: new Date().toISOString()
      };
    },
    drawRadar() {
      const ctx = this.$refs.radarCanvas;
      if (!ctx) return;
      this.destroyRadar();

      const C = this.radarColorsUniversal();

      const labels = ['Plánovanie', 'Vedomosti', 'Regulácia', 'Motívácia', 'Všeobecné'];
      const data = [
        this.valOr(this.scales.Planning,   0),
        this.valOr(this.scales.Knowledge,  0),
        this.valOr(this.scales.Regulation, 0),
        this.valOr(this.scales.Motivation, 0),
        this.valOr(this.scales.Global,     0)
      ];

        this.radar = new Chart(ctx, {
        type: 'radar',
        data: {
          labels,
          datasets: [{
            label: 'Challenging (1–5)',
            data,
            borderColor: C.border,
            backgroundColor: C.fill,
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: C.point,
            pointBorderColor: C.border,
            pointHoverBackgroundColor: C.point,
            pointHoverBorderColor: C.border,
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { labels: { color: C.text } },
            tooltip: { enabled: true }
          },
          scales: {
            r: {
              min: 1, max: 5,
              ticks: {
                stepSize: 1,
                color: C.text,
                backdropColor: 'rgba(0,0,0,0)' // žiadne sivé štvorčeky pod číslami
              },
              grid: { color: C.grid, lineWidth: 1 },
              angleLines: { color: C.angle, lineWidth: 1 },
              pointLabels: { color: C.text, font: { size: 12 } }
            }
          }
        }
      });
    },
    destroyRadar() { if (this.radar) { this.radar.destroy(); this.radar = null; } },
    emitAndBeacon(summary) {
      this.$emit('finalize', summary);
      try { localStorage.setItem('metacog_summary', JSON.stringify(summary)); } catch(e) {}
      if (this.beaconUrl) {
        try {
          const blob = new Blob([JSON.stringify(summary)], { type: 'application/json' });
          navigator.sendBeacon(this.beaconUrl, blob);
        } catch(e) {}
      }
    },
    onBeforeUnload() {
      if (this.phase === 'running' && !this.finalized) {
        const summary = this.summarize('abandon');
        this.emitAndBeacon(summary);
        this.finalized = true;
      }
    },
    onPageHide() {
      if (this.phase === 'running' && !this.finalized) {
        const summary = this.summarize('abandon');
        this.emitAndBeacon(summary);
        this.finalized = true;
      }
    },
    cleanup() {
      if (this.secTimer) clearInterval(this.secTimer);
      this.secTimer = null;
      window.removeEventListener('beforeunload', this.onBeforeUnload, true);
      window.removeEventListener('pagehide', this.onPageHide, true);
    },
    roundObj(obj, decimals = 3) {
      const k = Math.pow(10, decimals);
      return Object.fromEntries(
        Object.entries(obj || {}).map(([key, val]) => [
          key,
          (typeof val === 'number' && isFinite(val)) ? Math.round(val * k) / k : val
        ])
      );
    },
    getBaseTextColor() {
      const base = getComputedStyle(this.$el).color || '#222';
      return base; // môže byť "rgb(...)" alebo "rgba(...)" alebo "#hex"
    },
    toRGBA(color, alpha=1) {
      // prevedie rgb/rgba/#hex na rgba(r,g,b,a)
      const ctx = document.createElement('canvas').getContext('2d');
      ctx.fillStyle = color;            // nech prehliadač spraví parsing
      const parsed = ctx.fillStyle;     // vždy v podobe "rgb(r, g, b)"
      const m = parsed.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
      const [_, r, g, b] = m || [0, 34, 34, 34]; // fallback #222
      return `rgba(${r||34}, ${g||34}, ${b||34}, ${alpha})`;
    },
    radarColorsUniversal() {
      const text = this.getBaseTextColor();
      return {
        text,
        grid:  this.toRGBA(text, 0.22),
        angle: this.toRGBA(text, 0.30),
        // neutrálna, „bezbrandová“ akcentová farba (teal)
        border: '#14b8a6',                          // teal-500
        fill:   'rgba(20, 184, 166, 0.22)',         // priesvitná výplň
        point:  '#14b8a6'
      };
    },
    writeAnswer(_end_reason) {
      const _data = {
          id: 0,
          test_id: this.$route.params.id,
          score: -1 ,
          answers : JSON.stringify(this.responses), 
          final: _end_reason == 'completed' ? 1 : 0,
          rslt: JSON.stringify(this.roundObj(this.scales, 3)),
          agreement: this.agreement,
          time_length: this.startTime ? Math.round((Date.now() - this.startTime)/1000) : 0
        } 
        axios.post('/write-my-person-test-answer', _data, this.$store.getters.getAxiosConfig)
        .then(res => {
            // this.answer_id = res.data.answer_id
        })
        .catch(error => {
            this.$store.commit('setFeedback', error)
        })
    },
    closeTest() {
      this.$router.push({name:'personalisation_tests', params: {}}) 
    },
  },
  beforeUnmount() {
    this.cleanup();
    this.destroyRadar();
  }
};
</script>

<style scoped>
.max-w-5xl { max-width: 64rem; }
.max-w-xl  { max-width: 36rem; }
.p-4 { padding: 1rem; }
.mx-auto { margin-left: auto; margin-right: auto; }
.mb-2 { margin-bottom: .5rem; }
.mb-3 { margin-bottom: .75rem; }
.mt-4 { margin-top: 1rem; }
.text-xl { font-size: 1.25rem; line-height: 1.75rem; }
.font-semibold { font-weight: 600; }
.text-sm { font-size: .875rem; }
.text-xs { font-size: .75rem; }
.opacity-80 { opacity: .8; }
.cursor-pointer { cursor: pointer; }
.overflow-auto { overflow: auto; }

/* Tabuľka Likertu */
.table-wrapper { overflow-x: auto; }
.likert-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.likert-table th, .likert-table td { border: 1px solid #e5e7eb; padding: .5rem; }
.col-question { width: 60%; text-align: left; background: #fafafa; }
.col-opt { width: 8%; text-align: center; }
.qtext { text-align: left; }
.qid { font-weight: 600; margin-right: .25rem; }
.opt-cell { text-align: center; vertical-align: middle; }
.opt-cell input[type="radio"] { width: 1.1rem; height: 1.1rem; cursor: pointer; }

.th-num { font-weight: 700; }
.th-sub { font-size: .7rem; opacity: .8; }

/* Radar kontajner */
.max-w-xl { max-width: 36rem; }

.btn-bar { position: relative; }
.btn-bar .visible-disabled {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
</style>
